import { useEffect, useMemo, useState } from 'react';
import { Header, Loading, PointHistoryTab, Tabs, TecoAsset } from 'components';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getPointHistory } from 'api';
import { EPaymentType, EPointHistoryType } from 'types';
import { useNavigate } from 'react-router-dom';
import {
  PAGE_SIZE,
  queryKeys,
  routes,
  TEXT_STRING,
} from '../../../../constants';

const str = TEXT_STRING.POINTS;

function PointHistory() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState<EPaymentType>(EPaymentType.BANK);

  const {
    data: bankData,
    fetchNextPage: fetNextBankPage,
    hasNextPage: hasNextBankPage,
    isLoading: isBankLoading,
  } = useInfiniteQuery({
    queryKey: [queryKeys.BUY_POINTS_HISTORY, EPaymentType.BANK],
    queryFn: ({ pageParam }) =>
      getPointHistory(
        pageParam,
        PAGE_SIZE,
        false,
        EPaymentType.BANK,
        EPointHistoryType.ADDED,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
  });

  const {
    data: creditData,
    fetchNextPage: fetNextCreditPage,
    hasNextPage: hasNextCreditPage,
    isLoading: isCreditLoading,
  } = useInfiniteQuery({
    queryKey: [queryKeys.BUY_POINTS_HISTORY, EPaymentType.CARD],
    queryFn: ({ pageParam }) =>
      getPointHistory(
        pageParam,
        PAGE_SIZE,
        false,
        EPaymentType.CARD,
        EPointHistoryType.ADDED,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
  });

  const {
    data: cashData,
    fetchNextPage: fetNextCashPage,
    hasNextPage: hasNextCashPage,
    isLoading: isCashLoading,
  } = useInfiniteQuery({
    queryKey: [queryKeys.BUY_POINTS_HISTORY, EPaymentType.CASH],
    queryFn: ({ pageParam }) =>
      getPointHistory(
        pageParam,
        PAGE_SIZE,
        false,
        EPaymentType.CASH,
        EPointHistoryType.ADDED,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
    enabled: currentTab === EPaymentType.CASH,
  });

  const children = useMemo(
    () => (
      <div className="flex flex-col gap-8 pb-3">
        <TecoAsset />
        <Tabs
          className="mx-0.5"
          // 3 タブでも「クレジットカード」を 1 行に収める
          buttonClassName="!px-1"
          tabs={[
            {
              id: EPaymentType.BANK,
              title: str.BANK_TRANSFER,
            },
            {
              id: EPaymentType.CARD,
              title: str.CREDIT_CARD,
            },
            {
              id: EPaymentType.CASH,
              title: str.CASH_HANDOVER,
            },
          ]}
          textSize="text-[13px]"
          currentTab={currentTab}
          onChangeTab={(tab) => {
            setCurrentTab(tab as EPaymentType);
          }}
        />
      </div>
    ),
    [currentTab],
  );

  const bankHistory = useMemo(
    () => bankData?.pages.map((item) => item.data).flat() ?? [],
    [bankData?.pages],
  );

  const creditHistory = useMemo(
    () => creditData?.pages.map((item) => item.data).flat() ?? [],
    [creditData?.pages],
  );

  const bankRowCount = useMemo(
    () => bankHistory?.length + +hasNextBankPage,
    [hasNextBankPage, bankHistory?.length],
  );

  const creditRowCount = useMemo(
    () => creditHistory?.length + +hasNextCreditPage,
    [hasNextCreditPage, creditHistory?.length],
  );

  const cashHistory = useMemo(
    () => cashData?.pages.map((item) => item.data).flat() ?? [],
    [cashData?.pages],
  );

  const cashRowCount = useMemo(
    () => cashHistory?.length + +hasNextCashPage,
    [hasNextCashPage, cashHistory?.length],
  );

  const handleClickOnPointItem = (pointId: number, isBankType: boolean) => {
    if (!isBankType) return;
    navigate(routes.POINT_HISTORY_DETAIL.replace(':pointId', `${pointId}`));
  };

  useEffect(() => {
    queryClient.refetchQueries({ queryKey: [queryKeys.POINTS] });
  }, [queryClient]);

  return (
    <>
      <Header title={str.POINT_HISTORY_TITLE} />
      <div className="pt-8 px-4 pb-20 h-full flex flex-col">
        {(
          currentTab === EPaymentType.CASH
            ? isCashLoading
            : isBankLoading || isCreditLoading
        ) ? (
          <div className="flex justify-center py-9">
            <Loading />
          </div>
        ) : (
          <>
            {currentTab === EPaymentType.BANK ? (
              <PointHistoryTab
                children={children}
                data={bankHistory}
                fetchMore={fetNextBankPage}
                hasMore={hasNextBankPage}
                dataLength={bankHistory.length}
                rowCount={bankRowCount}
                onClickPointItem={handleClickOnPointItem}
              />
            ) : currentTab === EPaymentType.CASH ? (
              <PointHistoryTab
                children={children}
                data={cashHistory}
                fetchMore={fetNextCashPage}
                hasMore={hasNextCashPage}
                dataLength={cashHistory.length}
                rowCount={cashRowCount}
                onClickPointItem={handleClickOnPointItem}
              />
            ) : (
              <PointHistoryTab
                children={children}
                data={creditHistory}
                fetchMore={fetNextCreditPage}
                hasMore={hasNextCreditPage}
                dataLength={creditHistory.length}
                rowCount={creditRowCount}
                onClickPointItem={handleClickOnPointItem}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

export default PointHistory;
